import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import type { CryptoAsset, CryptoSearchData } from "../types";

type Props = {
  value: CryptoAsset;
  popular: CryptoAsset[];
  onSelect: (asset: CryptoAsset, quote: "USD" | "BRL") => void;
};

export function CryptoAssetPicker({ value, popular, onSelect }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CryptoAsset[]>(popular);
  const [status, setStatus] = useState("Escolha o ativo e a moeda de referência.");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    function outside(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function escape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const needle = query.trim();
    if (needle.length < 2) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      setStatus("Pesquisando ativos…");
      api<CryptoSearchData>(`/api/crypto-search?q=${encodeURIComponent(needle)}`, { signal: controller.signal })
        .then((payload) => {
          setResults(payload.assets);
          setStatus(payload.assets.length ? `${payload.assets.length} resultado(s) · ${payload.source}` : "Nenhum ativo encontrado.");
        })
        .catch((error: Error) => {
          if (error.name !== "AbortError") {
            setResults([]);
            setStatus(error.message);
          }
        })
        .finally(() => setLoading(false));
    }, 280);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, popular, query]);

  const normalizedQuery = query.trim().toLowerCase();
  const visibleResults = normalizedQuery.length < 2
    ? popular.filter((asset) => `${asset.symbol} ${asset.name}`.toLowerCase().includes(normalizedQuery))
    : results;
  const visibleStatus = normalizedQuery.length < 2
    ? normalizedQuery ? "Digite mais um caractere para pesquisar o mercado." : "Ativos mais usados"
    : status;

  function choose(asset: CryptoAsset, quote: "USD" | "BRL") {
    onSelect(asset, quote);
    setOpen(false);
    setQuery("");
  }

  return (
    <div ref={rootRef} className="market-picker">
      <span className="market-picker-label">Comparar</span>
      <button
        type="button"
        className="market-picker-trigger"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen((current) => !current);
          window.setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        <b>{value.symbol}</b><span>{value.name}</span><i>⌄</i>
      </button>
      {open && (
        <section className="market-picker-popover" aria-label="Pesquisar criptomoeda">
          <div className="market-search-field">
            <span aria-hidden="true">⌕</span>
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Digite ADA, Cardano, Bitcoin…"
              aria-label="Nome ou sigla da criptomoeda"
              role="searchbox"
            />
            {normalizedQuery.length >= 2 && loading && <i className="market-search-spinner" aria-label="Pesquisando" />}
          </div>
          <div className="market-search-status" aria-live="polite">{visibleStatus}</div>
          <div className="market-search-results">
            {visibleResults.map((asset) => (
              <article key={asset.id} className="market-search-result">
                <div className="market-result-identity">
                  <span>{asset.symbol.slice(0, 4)}</span>
                  <div><strong>{asset.name}</strong><small>{asset.symbol}{asset.marketCapRank ? ` · ranking #${asset.marketCapRank}` : " · sem ranking"}</small></div>
                </div>
                <div className="market-pair-actions" aria-label={`Pares de referência para ${asset.name}`}>
                  <button type="button" onClick={() => choose(asset, "USD")}>{asset.symbol}/USD</button>
                  <button type="button" onClick={() => choose(asset, "BRL")}>{asset.symbol}/BRL</button>
                </div>
              </article>
            ))}
          </div>
          <p className="market-search-note">USD e BRL são referências de preço. A fonte e o método efetivamente usados aparecem abaixo do gráfico.</p>
        </section>
      )}
    </div>
  );
}
